/**
 * The selection toolbar (plan 7.4) as data, shared by the web toolbar
 * (anchored to the bridge's rect or the Tiptap selection) and the native
 * one (above the keyboard accessory row): its actions in reading order
 * (mirrored in RTL by the layout direction, not by reordering), what each
 * sends, and where the toolbar sits over the selection.
 */
import type { ArtifactContext, ArtifactSelection } from '@tabbio/types';
import { armArtifactAsk, artifactAskChipText, sendArtifactQuickAction } from '@/lib/artifact-ask';
import { trackArtifactSelectionAction } from '../artifact-analytics';

export type QuickAction = 'shorter' | 'longer' | 'casual' | 'formal' | 'suggest';

export type SelectionAction = 'ask' | QuickAction;

/** The toolbar's buttons in reading order; Tone holds More casual and More formal. */
export const SELECTION_TOOLBAR_ITEMS: readonly (
  | { kind: 'action'; action: 'ask' | 'shorter' | 'longer' | 'suggest'; label: string }
  | { kind: 'menu'; label: string; actions: readonly { action: 'casual' | 'formal'; label: string }[] }
)[] = [
  { kind: 'action', action: 'ask', label: 'chat.artifacts.toolbar.ask' },
  { kind: 'action', action: 'shorter', label: 'chat.artifacts.toolbar.shorter' },
  { kind: 'action', action: 'longer', label: 'chat.artifacts.toolbar.longer' },
  {
    kind: 'menu',
    label: 'chat.artifacts.toolbar.tone',
    actions: [
      { action: 'casual', label: 'chat.artifacts.toolbar.casual' },
      { action: 'formal', label: 'chat.artifacts.toolbar.formal' },
    ],
  },
  { kind: 'action', action: 'suggest', label: 'chat.artifacts.toolbar.suggest' },
];

/** The instruction a quick action sends, as a copy key. */
export const QUICK_ACTION_PROMPTS: Record<QuickAction, string> = {
  shorter: 'chat.artifacts.prompts.shorter',
  longer: 'chat.artifacts.prompts.longer',
  casual: 'chat.artifacts.prompts.casual',
  formal: 'chat.artifacts.prompts.formal',
  suggest: 'chat.artifacts.prompts.suggest',
};

export type SelectionTarget = {
  artifactId: string;
  /** The version on screen; omitted means the head. */
  versionId?: string | null;
  /** The artifact's chat thread, so a quick action lands in it. */
  threadId: string | null;
  selection: ArtifactSelection;
  /** What the chip quotes. */
  label: string;
};

export function selectionContext(target: Pick<SelectionTarget, 'artifactId' | 'versionId' | 'selection'>): ArtifactContext {
  return {
    artifactId: target.artifactId,
    ...(target.versionId ? { versionId: target.versionId } : {}),
    selection: target.selection,
  };
}

/**
 * Runs a toolbar action: Ask Tabbio arms the chip and focuses the composer;
 * a quick action sends its instruction at once with the same context.
 * `translate` turns the prompt key into the reader's language. Each action
 * is counted (`artifact_selection_action`, the action and the kind only).
 */
export function runSelectionAction(
  action: SelectionAction,
  target: SelectionTarget,
  translate: (key: string) => string,
): { sent: string | null } {
  const context = selectionContext(target);
  if (target.selection.kind !== 'image') trackArtifactSelectionAction(action, target.selection.kind);
  if (action === 'ask') {
    armArtifactAsk(context, target.label);
    return { sent: null };
  }
  const text = translate(QUICK_ACTION_PROMPTS[action]);
  sendArtifactQuickAction({ text, context, threadId: target.threadId });
  return { sent: text };
}

/** The chip's words for a target (the selection, trimmed to 60 characters). */
export function selectionChipLabel(target: Pick<SelectionTarget, 'label'>): string {
  return artifactAskChipText(target.label);
}

export type ToolbarPlacement = { left: number; top: number; placement: 'above' | 'below' };

/**
 * Where the toolbar goes: centred over the selection, above it when there
 * is room (else below), kept inside the container with an 8 px margin.
 * Everything is in the container's coordinates.
 */
export function selectionToolbarPlacement(input: {
  rect: { x: number; y: number; width: number; height: number };
  container: { width: number; height: number };
  toolbar: { width: number; height: number };
  gap?: number;
  margin?: number;
}): ToolbarPlacement {
  const gap = input.gap ?? 8;
  const margin = input.margin ?? 8;
  const { rect, container, toolbar } = input;
  const centre = rect.x + rect.width / 2;
  const maxLeft = Math.max(margin, container.width - toolbar.width - margin);
  const left = Math.min(maxLeft, Math.max(margin, Math.round(centre - toolbar.width / 2)));
  const above = rect.y - gap - toolbar.height;
  if (above >= margin) return { left, top: Math.round(above), placement: 'above' };
  const below = rect.y + rect.height + gap;
  const maxTop = Math.max(margin, container.height - toolbar.height - margin);
  return { left, top: Math.round(Math.min(below, maxTop)), placement: 'below' };
}
